import {useCallback, useEffect, useRef} from 'react';
import {useDispatch} from 'react-redux';
import {
  useLazyGetUserSubscriptionQuery,
  useMtnRefreshMutation,
} from '@src/Api/TraceApi/traceApi';
import useIsLoggedIn from '@src/Providers/AuthProvider/useIsLoggedIn';
import {useAppSelector} from '@src/Store/hooks';
import memberSlice, {IMemberSubscription} from '@src/Store/Slices/Member.slice';
import {
  CARRIER_FROM_EXTERNAL_ID,
  CARRIER_TO_DISPLAY_PLAN,
} from '@src/Hooks/useMemberSubscription';
import {MTN_TEST_LOGIN_KEY} from '@src/Configs/mtnConfig';
import {applyMtnTestLogin, readMtnTestLogin} from './useMtnLoginCompletion';

const REFRESH_INTERVAL_MS = 45 * 60 * 1000; // 45 minutes
const EXPIRY_BUFFER_MS = 2 * 60 * 60 * 1000; // 2 hours before expiry → refresh

/**
 * Periodically re-checks the MTN subscription with Penrose via
 * POST /auth/mtn/refresh (backend reads the msisdn from Firebase claims and
 * updates the subscription).
 *
 * Unlike Vodacom, an inactive MTN user stays logged in as a free user, so we
 * only reload the subscription into the store; the paywall does the rest.
 */
export function useMtnSessionRefresh() {
  const [mtnRefresh] = useMtnRefreshMutation();
  const [getUserSubscription] = useLazyGetUserSubscriptionQuery();
  const dispatch = useDispatch();
  const {isLoggedIn} = useIsLoggedIn();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasRunInitialRef = useRef(false);

  const memberSubscription = useAppSelector(
    state => state.member.memberSubscription,
  );
  const isMtn = memberSubscription?.carrier === 'mtn';

  const doRefresh = useCallback(async () => {
    // Test login (backend MTN_DB_DISABLED): ask with the mtnToken, keep the result locally
    const testLogin = readMtnTestLogin();
    if (testLogin) {
      try {
        const response = await mtnRefresh({mtnToken: testLogin.mtnToken}).unwrap();
        const updated = {
          ...testLogin,
          subscriptionStatus:
            response.action === 'refreshed'
              ? response.subscriptionStatus
              : ('CANCELLED' as const),
        };
        localStorage.setItem(MTN_TEST_LOGIN_KEY, JSON.stringify(updated));
        applyMtnTestLogin(dispatch, updated);
      } catch (err) {
        console.warn('[MTN Refresh] Test refresh failed:', err);
      }
      return;
    }

    try {
      const response = await mtnRefresh().unwrap();
      console.log('[MTN Refresh]', response.action);

      const {data} = await getUserSubscription();
      if (!data) return;
      const rawProductId = data.externalID;
      const subscription: IMemberSubscription = {
        memberId: data.memberId,
        subscriptionStart: data.subscriptionStart.toString(),
        subscriptionEnd: data.subscriptionEnd?.toString(),
        id: data._id,
        deviceType: data.deviceType,
        subscriptionType: data.subscriptionTypeId,
        productId: CARRIER_TO_DISPLAY_PLAN[rawProductId] || rawProductId,
        cancelRequested: data.cancelRequested,
        carrier: CARRIER_FROM_EXTERNAL_ID[rawProductId],
      };
      dispatch(memberSlice.actions.setMemberSubscription(subscription));
    } catch (err) {
      console.warn('[MTN Refresh] Failed:', err);
    }
  }, [mtnRefresh, getUserSubscription, dispatch]);

  useEffect(() => {
    if (!isLoggedIn || !isMtn) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      hasRunInitialRef.current = false;
      return;
    }

    if (!hasRunInitialRef.current) {
      hasRunInitialRef.current = true;
      const subEnd = memberSubscription?.subscriptionEnd;
      if (subEnd && new Date(subEnd).getTime() - Date.now() < EXPIRY_BUFFER_MS) {
        doRefresh();
      }
    }

    intervalRef.current = setInterval(doRefresh, REFRESH_INTERVAL_MS);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isLoggedIn, isMtn, doRefresh, memberSubscription?.subscriptionEnd]);
}

export default useMtnSessionRefresh;
